/**
 * RequirePermission — shared admin-portal permission gate primitive.
 *
 * Mirrors the backend `UserPermissions.has()` decision (defined in
 * `packages/authz/src/permissions/user-permissions.vo.ts`). Order:
 *
 *   1. `isSuperUser` short-circuit (per the current-org membership) → render
 *      children. This is the wire mirror of the backend's super_user
 *      short-circuit and removes the operational burden of "remember to
 *      grant every new permission to super_user" — the role-driven flag
 *      stays in lockstep with the backend, no role_permissions row needed
 *      for super_user to see a gated affordance.
 *   2. `platform.role === 'root'` short-circuit (session-wide) → render
 *      children. ADR 0016 — Root is an unconditional supra-tenant grant.
 *   3. `platform.role === 'support'` short-circuit → render children
 *      unless the required slug starts with `identity.platform.` (ADR 0016).
 *   4. Slug membership check on the current-org membership's
 *      `permissions[]` array.
 *
 * Per CLAUDE.md "Frontend (Sparta)" non-negotiable rule §3, the `slug`
 * prop is typed against `PermissionSlug` from `@artemis/contracts`,
 * which is generated from the backend permission registry. String
 * literals not in the union fail typecheck.
 *
 * Source: composes `useSession` + `useCurrentOrg` from `shared/hooks/`
 * to extract the raw membership object from `/auth/me.memberships`.
 * Deliberately re-implements the narrowing here (rather than reusing
 * `useCurrentMembershipView` from the identity module) because Hybrid Z
 * forbids `shared/` importing from `modules/` (rule §6) — a shared
 * primitive cannot depend on a slice's internals or barrel.
 *
 * During the pre-load window (session loading / org not yet resolved),
 * the gate renders the fallback. The cost of a missing-then-appearing
 * affordance is a layout shift; the cost of a wrongly-rendered Edit
 * button is a 403 on save — the former is the cheaper failure mode.
 */

import * as React from 'react';
import type { PermissionSlug } from '@artemis/contracts';
import { useSession } from '../hooks/useSession.js';
import { useCurrentOrg } from '../hooks/useCurrentOrg.js';

export interface RequirePermissionProps {
  readonly slug: PermissionSlug;
  readonly fallback?: React.ReactNode;
  readonly children: React.ReactNode;
}

interface MembershipView {
  readonly isSuperUser: boolean;
  readonly permissions: readonly string[];
}

interface PlatformView {
  readonly role: 'root' | 'support';
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === 'object' && !Array.isArray(value);
}

function isString(value: unknown): value is string {
  return typeof value === 'string';
}

function readPlatformView(user: unknown): PlatformView | null {
  if (!isPlainObject(user)) return null;
  const platform = user.platform;
  if (!isPlainObject(platform)) return null;
  if (platform.role === 'root' || platform.role === 'support') {
    return { role: platform.role };
  }
  return null;
}

function readMembershipView(
  user: unknown,
  organizationId: string,
): MembershipView | null {
  if (!isPlainObject(user)) return null;
  const memberships = user.memberships;
  if (!Array.isArray(memberships)) return null;
  const match = memberships.find(
    (m): m is Record<string, unknown> =>
      isPlainObject(m) && m.organizationId === organizationId,
  );
  if (match === undefined) return null;
  const permissions = Array.isArray(match.permissions)
    ? match.permissions.filter(isString)
    : [];
  const isSuperUser = match.isSuperUser === true;
  return { isSuperUser, permissions };
}

export function hasPermission(
  user: unknown,
  organizationId: string,
  slug: string,
): boolean {
  const membership = readMembershipView(user, organizationId);
  if (membership === null) return false;
  // Mirror UserPermissions.has() strict ordering — see ADR 0016 / SCRUM-16.
  if (membership.isSuperUser) return true;
  const platform = readPlatformView(user);
  if (platform !== null) {
    if (platform.role === 'root') return true;
    if (platform.role === 'support') {
      return !slug.startsWith('identity.platform.');
    }
  }
  return membership.permissions.includes(slug);
}

export function RequirePermission({
  slug,
  fallback = null,
  children,
}: RequirePermissionProps): React.ReactElement | null {
  const session = useSession();
  const orgState = useCurrentOrg();

  if (session.status !== 'authenticated' || orgState === null) {
    return <>{fallback}</>;
  }

  if (
    hasPermission(session.user, orgState.membership.organizationId, slug)
  ) {
    return <>{children}</>;
  }
  return <>{fallback}</>;
}
